import { useLocale } from '@lessonup/client-integration';
import {
  Card,
  IconSortAscending,
  IconSortDescending,
  Introduction,
  SortableTableRowData,
  spacing,
  styled,
  SvgComponent,
  Table,
  TableCellsHeader,
  useTranslation,
} from '@lessonup/ui-components';
import { localeToIsoLocaleString } from '@lessonup/utils';
import { isNumber } from 'lodash';
import React, { useCallback, useState } from 'react';
import { i18nextNamespace } from '../../utils/consts';
import { QuestionType, ReportQuestion } from '../../utils/types';

export type SortField = 'questionTitle' | 'slideNumber' | 'questionType' | 'correct' | 'incorrect' | 'unanswered';
export type SortDirection = 'ASC' | 'DESC';

export type QuestionListCardProps = {
  questions: ReportQuestion[];
  className?: string;
};

export const QuestionsListCard = ({ questions, className }: QuestionListCardProps): React.JSX.Element => {
  const { t } = useTranslation(i18nextNamespace, { keyPrefix: 'questionsListCard' });
  const locale = useLocale();
  const [sortField, setSortField] = useState<SortField>('unanswered');
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      // Always start with ASC when switching to a new field
      setSortDirection('ASC');
    }
  };

  const getEndIconForTableHeader = (field: SortField): SvgComponent | undefined => {
    if (sortField !== field) {
      return undefined;
    }

    return sortDirection === 'ASC' ? IconSortAscending : IconSortDescending;
  };

  /**
   * Format the percentage we get from the server to a locale percentage string with no decimal places
   */
  const percentageFormatter = useCallback(
    (percentage: number): string =>
      new Intl.NumberFormat(localeToIsoLocaleString(locale), {
        style: 'percent',
        maximumFractionDigits: 0,
      }).format(percentage / 100),
    [locale]
  );

  const getQuestionTypeDisplayText = useCallback(
    (type: QuestionType): string => {
      switch (type) {
        case 'MULTIPLE_CHOICE':
          return t('table.questionType.multipleChoice');
        case 'OPEN_QUESTION':
          return t('table.questionType.openQuestion');
        case 'WORD_CLOUD':
          return t('table.questionType.wordCloud');
        default:
          return type;
      }
    },
    [t]
  );

  const header: TableCellsHeader = {
    cells: [
      {
        text: t('table.header.question'),
        onClick: () => handleSort('questionTitle'),
        endSvgIcon: getEndIconForTableHeader('questionTitle'),
      },
      {
        text: t('table.header.slide'),
        onClick: () => handleSort('slideNumber'),
        endSvgIcon: getEndIconForTableHeader('slideNumber'),
      },
      {
        text: t('table.header.type'),
        onClick: () => handleSort('questionType'),
        endSvgIcon: getEndIconForTableHeader('questionType'),
      },
      {
        text: t('table.header.correct'),
        onClick: () => handleSort('correct'),
        endSvgIcon: getEndIconForTableHeader('correct'),
      },
      {
        text: t('table.header.incorrect'),
        onClick: () => handleSort('incorrect'),
        endSvgIcon: getEndIconForTableHeader('incorrect'),
      },
      {
        text: t('table.header.unanswered'),
        onClick: () => handleSort('unanswered'),
        endSvgIcon: getEndIconForTableHeader('unanswered'),
      },
    ],
  };

  const rows: SortableTableRowData<SortField>[] = questions.map(
    ({
      id,
      questionTitle,
      slideNumber,
      questionType,
      percentageStudentsAnsweredCorrectly,
      percentageStudentsAnsweredIncorrectly,
      percentageStudentsUnanswered,
    }: ReportQuestion) => ({
      id,
      ariaLabel: t('table.ariaRow', { questionTitle }),
      cells: [
        {
          text: questionTitle || t('N/A'),
          sorting: {
            key: 'questionTitle',
            value: questionTitle || '',
          },
        },
        {
          text: slideNumber.toLocaleString(),
          sorting: {
            key: 'slideNumber',
            value: slideNumber,
          },
        },
        {
          text: questionType ? getQuestionTypeDisplayText(questionType) : t('N/A'),
          sorting: {
            key: 'questionType',
            value: questionType ? getQuestionTypeDisplayText(questionType) : '',
          },
        },
        {
          text: isNumber(percentageStudentsAnsweredCorrectly)
            ? percentageFormatter(percentageStudentsAnsweredCorrectly)
            : t('N/A'),
          sorting: {
            key: 'correct',
            value: isNumber(percentageStudentsAnsweredCorrectly) ? percentageStudentsAnsweredCorrectly : -1,
          },
        },
        {
          text: isNumber(percentageStudentsAnsweredIncorrectly)
            ? percentageFormatter(percentageStudentsAnsweredIncorrectly)
            : t('N/A'),
          sorting: {
            key: 'incorrect',
            value: isNumber(percentageStudentsAnsweredIncorrectly) ? percentageStudentsAnsweredIncorrectly : -1,
          },
        },
        {
          text: isNumber(percentageStudentsUnanswered) ? percentageFormatter(percentageStudentsUnanswered) : t('N/A'),
          sorting: {
            key: 'unanswered',
            value: isNumber(percentageStudentsUnanswered) ? percentageStudentsUnanswered : -1,
          },
        },
      ],
    })
  );

  const compareValues = (a: string | number, b: string | number): number => {
    if (typeof a === 'string' && typeof b === 'string') {
      return sortDirection === 'ASC'
        ? a.localeCompare(b, undefined, { sensitivity: 'base' })
        : b.localeCompare(a, undefined, { sensitivity: 'base' });
    }

    const aNum = typeof a === 'number' ? a : Number(a) || 0;
    const bNum = typeof b === 'number' ? b : Number(b) || 0;

    return sortDirection === 'ASC' ? aNum - bNum : bNum - aNum;
  };

  const sortedRows = [...rows].sort((a, b) => {
    const aCell = a.cells.find((cell) => cell.sorting?.key === sortField);
    const bCell = b.cells.find((cell) => cell.sorting?.key === sortField);

    if (!aCell?.sorting || !bCell?.sorting) return 0;

    return compareValues(aCell.sorting.value, bCell.sorting.value);
  });

  return (
    <Card showBorder={false} className={className}>
      <IntroductionWrapper>
        <Introduction alignment="start" size="M" headline={t('headline')} bodyText={t('bodyText')} />
      </IntroductionWrapper>
      <TableWrapper>
        <Table caption={t('caption')} header={header} rows={sortedRows} interactivity={false} hideLastBorder />
      </TableWrapper>
    </Card>
  );
};

const IntroductionWrapper = styled.div`
  padding: ${spacing.size16};
`;

const TableWrapper = styled.div`
  overflow: auto;
`;
